import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { ChipPopover, SourcesPopover, Tag } from '@/components';
import { useSourcesStore } from '@/stores';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface ExplicitSourcesChipProps {
    sourceIds: string[];
    onRemoveSource?: (id: string) => void;
    tagVariant?: 'base' | 'plain';
}

export function ExplicitSourcesChip({
    sourceIds,
    onRemoveSource,
    tagVariant = 'base',
}: ExplicitSourcesChipProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { getSourceById } = useSourcesStore();
    const [popoverOpen, setPopoverOpen] = useState(false);

    if (sourceIds.length === 0) return null;

    if (sourceIds.length === 1) {
        const source = getSourceById(sourceIds[0]);
        if (!source) return null;

        return (
            <Tag
                data-testid="filter-chip-sources"
                variant={tagVariant}
                size="sm"
                leadingIcon={<SecurityCameraIconMirrored weight="fill" />}
                onRemove={
                    onRemoveSource
                        ? () => onRemoveSource(sourceIds[0])
                        : undefined
                }
            >
                {source.displayName ?? source.name}
            </Tag>
        );
    }

    return (
        <ChipPopover
            popoverOpen={popoverOpen}
            onPopoverOpenChange={setPopoverOpen}
            chip={
                <Tag
                    data-testid="filter-chip-sources"
                    variant={tagVariant}
                    size="sm"
                    leadingIcon={<SecurityCameraIconMirrored weight="fill" />}
                    trailingIcon={
                        popoverOpen ? <CaretUpIcon /> : <CaretDownIcon />
                    }
                    onClick={() => setPopoverOpen(!popoverOpen)}
                >
                    {t('filters.selectedSources', { count: sourceIds.length })}
                </Tag>
            }
        >
            <SourcesPopover
                sourceIds={sourceIds}
                isExclusionMode={false}
                onRemoveSource={onRemoveSource}
            />
        </ChipPopover>
    );
}
